import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Spin, message } from "antd";
import { ErrorElement } from "common/components/StyledElements";
import APIConstants from "common/constants/APIConstants";
import COLORS from "common/constants/Colors";
import { phoneNumberRegex } from "common/constants/regex";
import { useAuthContext } from "common/contexts/AuthContext";
import LoginIcon from "common/icons/LoginPageLoginIcon.svg";
import LoginUserNameInput from "common/icons/LoginUserNameInput.svg";
import LoginUserPasswordInput from "common/icons/LoginUserPasswordInput.svg";
import LoginUserVIewPassword from "common/icons/LoginViewPasswordIcon.svg";
import NewEnneaLogo from "common/icons/valueMrp_logo.png";
import { isRoleGranite, validateNumberInput } from "common/utils";
import { EventType, ReactGATracker } from "common/utils/analyticsTracker";
import { api } from "common/utils/APIMethods";
import { MESSAGE_TYPE, showMessage } from "common/utils/message";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";

const MainContainer = styled.div`
  display: flex;
  height: 100vh;
`;

export const SignInImageContainer = styled.img`
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  width: 50vw;
  float: left;
  height: 100vh;
  object-fit: cover;
  @media screen and (max-width: 992px) {
    display: none;
  }
`;

export const InputElement = styled.input`
  border: 0px;
  outline: none;
  width: 20vw;
  color: ${COLORS.dark_shaded_grey};
  font-family: "Lato";
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
  ::placeholder {
    color: ${(props) =>
      props?.error ? COLORS.salmon_pink : COLORS.dusty_grey};
  }
  @media screen and (max-width: 992px) {
    width: 50vw;
  }
`;

const Image = styled.img`
  width: auto;
  height: 30%;
`;

export const LoginBodyContainer = styled.div`
  background: ${COLORS.login_body_gradient};
  width: 100%;
  float: right;
  height: 100vh;
  display: flex;
  /* justify-content: space-between; */
  gap: 15px;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  @media screen and (max-width: 992px) {
    width: 100dvw;
  }
`;

const WelcomeToContainer = styled.div`
  color: ${COLORS.dark_teal};
  font-family: "Poppins";
  font-size: calc(3.91363rem * 0.7352);
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  letter-spacing: calc(-0.15656rem * 0.7352);
  margin-top: 0 px;
  @media screen and (max-width: 800px) {
    padding-top: 50px;
  }
`;

const HomeIconContainer = styled.img`
  width: 30px;
  height: 30px;
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
  @media screen and (max-width: 800px) {
    top: 10px;
    right: 10px;
  }
`;

export const ValuemediLogoContainer = styled.div`
  width: calc(30.75rem * 0.7352);
  height: calc(7.6875rem * 0.7352);
  flex-shrink: 0;
  margin-bottom: calc(3.94rem * 0.7352);
  margin-top: 5px;
`;

export const LoginDetailsInputContainer = styled.div`
  width: calc(30.75rem * 0.7352);
  height: calc(4.875rem * 0.7352);
  margin-bottom: 1rem;
  padding: 1.31rem;
  display: flex;
  column-gap: 1.31rem;
  align-items: center;
  flex-shrink: 0;
  border-radius: 0.75rem;
  background: ${COLORS.white};
  box-shadow: 0px 3px 18px -6px rgba(1, 191, 185, 0.4);
  @media screen and (max-width: 992px) {
    width: calc(26.75rem * 0.7352);
  }
`;

export const BottomTextContainer = styled.div`
  display: flex;
  gap: 4rem;
  color: ${COLORS.dark_teal};
  font-family: "Poppins";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
  cursor: pointer;
`;

const RegisterAndForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: calc(30.75rem * 0.7352);
  @media screen and (max-width: 992px) {
    width: calc(26.75rem * 0.7352);
  }
`;

export const LoginDetailsIconsContainer = styled.img`
  height: 1.75rem;
  width: 1.75rem;
  ${(props) =>
    props.password &&
    css`
      margin-left: auto;
      cursor: pointer;
    `}
`;

export const UserLoginButton = styled(Button)`
  && {
    width: calc(30.75rem * 0.7352);
    height: calc(4.875rem * 0.7352);
    flex-shrink: 0;
    border-radius: 0.375rem;
    border: 1.5px solid rgba(0, 148, 143, 0.3);
    background: ${COLORS.login_button_gradient};
    box-shadow: 0px 6px 9px -4.5px rgba(0, 191, 185, 0.6);
    color: ${COLORS.white};
    font-family: "Poppins";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 1.875rem;
    padding: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    :disabled {
      opacity: 0.5;
      pointer-events: none;
    }
    @media screen and (max-width: 992px) {
      width: calc(26.75rem * 0.7352);
    }
  }
`;

export const LoginOptionButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: calc(30.75rem * 0.7352);
  @media screen and (max-width: 992px) {
    width: calc(26.75rem * 0.7352);
  }
`;

export const LoginOptionButton = styled(BottomTextContainer)`
  margin-bottom: 0.5rem;
  align-self: flex-end;
`;

export const CustomError = styled(ErrorElement)`
  width: calc(30.75rem * 0.7352);
  margin-bottom: 5px;
  text-align: left;
`;

const Login = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    isAutoOtpGeneration = false,
    phoneNumber = "",
    username = ""
  } = state ?? {};
  const { dispatch, authConfig } = useAuthContext();
  const { loginURL, loginWithTokenURL, otpGenerate } = APIConstants;
  const [searchParams] = useSearchParams();
  const apiToken = searchParams.get("apiToken");
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showMobileNumberLogin, setShowMobileNumberLogin] = useState(false);
  const autoOtpGeneratedRef = useRef(false);

  const {
    isLoading: tokenLoginIsLoading,
    isSuccess,
    isError,
    error,
    data: loginResponse
  } = useQuery({
    queryKey: ["loginWithToken"],
    queryFn: () =>
      api({
        url: loginWithTokenURL,
        method: "POST",
        body: {
          token: apiToken
        },
        checkAuth: false
      }),
    enabled: !!apiToken
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch({ type: "onLogin", payload: loginResponse });

      if (isRoleGranite(loginResponse?.role?.role)) {
        navigate("/history", { replace: true });
      } else {
        navigate("/orders/list/default", { replace: true });
      }
    }
    if (isError) {
      showMessage(
        error?.message ?? "check_username_password",
        MESSAGE_TYPE.errorTranslate
      );
    }
  }, [isSuccess, isError, error, loginResponse, navigate, dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues
  } = useForm({
    mode: "onChange",
    defaultValues: {
      phoneNumber,
      username,
      password: ""
    }
  });

  useEffect(() => {
    if (authConfig.sessionId && !apiToken) {
      navigate("/history", { replace: true });
    }
  }, [apiToken, authConfig.sessionId, navigate]);

  const { isPending, mutate } = useMutation({
    mutationFn: (formData) =>
      api({
        url: loginURL,
        method: "POST",
        body: formData,
        checkAuth: false
      }),
    onSuccess: (response) => {
      dispatch({ type: "onLogin", payload: response });
      if (isRoleGranite(loginResponse?.role?.role)) {
        navigate("/history", { replace: true });
      } else {
        navigate("/orders/list/default", { replace: true });
      }
    },
    onError: (response) => {
      showMessage(
        response?.message ?? "check_username_password",
        MESSAGE_TYPE.errorTranslate
      );
    }
  });

  const onSubmit = (formData) => {
    ReactGATracker(EventType.LOGIN_TO_VM_BUTTON_CLICK_WITH_USER_NAME);
    mutate(formData);
  };

  const { isPending: sendingOTP, mutate: otpGenerationMutation } = useMutation({
    mutationFn: (formData) =>
      api({
        url: `${otpGenerate}/${parseInt(formData.phoneNumber, 10)}`
      }),
    onSuccess: (response) => {
      showMessage("otp_send_successful", MESSAGE_TYPE.successTranslate);
      navigate("/reset", {
        state: {
          phoneNumber: getValues("phoneNumber"),
          sessionId: response.sessionId,
          mode: 1,
          isPhoneNumberLogin: true
        }
      });
    },
    onError: (err) => {
      message.error(err.message);
    }
  });

  const onPhoneNumberLogin = (formData) => {
    ReactGATracker(EventType.LOGIN_TO_VM_BUTTON_CLICK_WITH_PHONE_NUMBER);
    otpGenerationMutation(formData);
  };

  const onChangeShowPassword = () => {
    if (showPassword) {
      ReactGATracker(EventType.LOGIN_HIDE_PASSWORD_CLICK);
    } else {
      ReactGATracker(EventType.LOGIN_SHOW_PASSWORD_CLICK);
    }
    setShowPassword(!showPassword);
  };

  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit(showMobileNumberLogin ? onPhoneNumberLogin : onSubmit)();
    }
  };

  const registerOptions = {
    required: t("enter_phone"),
    pattern: {
      value: phoneNumberRegex,
      message: t("enter_valid_phone")
    }
  };

  useEffect(() => {
    if (isAutoOtpGeneration && !autoOtpGeneratedRef.current) {
      setShowMobileNumberLogin(true);
      otpGenerationMutation({ phoneNumber: getValues("phoneNumber") });
      autoOtpGeneratedRef.current = true;
    }
  }, [getValues, isAutoOtpGeneration, otpGenerationMutation]);

  if (tokenLoginIsLoading) {
    return <Spin />;
  }

  return (
    <>
      <title>ValueMRP | Login</title>
      {/* <picture>
        <source srcSet={WebPLoginPagePharmacyImage} type="image/webp" />
        <SignInImageContainer
          src={LoginPagePharmacyImage}
          about="LOGIN_IMAGE"
          alt="ValueMedi Retailer/Distributor/Representative"
        />
      </picture> */}

      <LoginBodyContainer>
        {/* <HomeIconContainer
          src={HomeIcon}
          onClick={() => navigate("/home")}
          alt="ValueMedi Home Page Navigation"
        /> */}
        <WelcomeToContainer isPhoneNumberLogin={showMobileNumberLogin}>
          <Trans i18nKey="welcome_to_ValueMrp" />
        </WelcomeToContainer>
        {/* <ValuemediLogoContainer> */}
        <Image src={NewEnneaLogo} alt="logo" />
        {/* </ValuemediLogoContainer> */}
        {showMobileNumberLogin ? (
          <>
            <LoginDetailsInputContainer>
              <div>+91</div>
              <InputElement
                onKeyDown={validateNumberInput}
                {...register("phoneNumber", registerOptions)}
                error={errors?.phoneNumber}
                placeholder={t("enter_your_phone_number")}
                onKeyPress={handleEnterKeyPress}
                onBlur={(e) => setValue("phoneNumber", e.target.value.trim())}
                onFocus={() => {
                  ReactGATracker(
                    EventType.LOGIN_PHONE_NUMBER_INPUT_FIELD_CLICK
                  );
                }}
              />
            </LoginDetailsInputContainer>
            {errors.phoneNumber && (
              <CustomError>{errors.phoneNumber.message}</CustomError>
            )}
          </>
        ) : (
          <>
            <LoginDetailsInputContainer>
              <LoginDetailsIconsContainer
                src={LoginUserNameInput}
                alt="ValueMedi Retailer/Distributor/Representative icon"
              />
              <InputElement
                {...register("username", {
                  required: t("enter_your_username")
                })}
                placeholder={t("enter_your_username")}
                error={errors?.username}
                onKeyPress={handleEnterKeyPress}
                onBlur={(e) => setValue("username", e.target.value.trim())}
                onFocus={() => {
                  ReactGATracker(EventType.LOGIN_WITH_USER_NAME_FIELD_CLICK);
                }}
              />
            </LoginDetailsInputContainer>
            <LoginDetailsInputContainer>
              <LoginDetailsIconsContainer
                src={LoginUserPasswordInput}
                alt="ValueMedi user password icon"
              />
              <InputElement
                {...register("password", {
                  required: t("enter_your_password")
                })}
                type={showPassword ? "text" : "password"}
                error={errors?.password}
                placeholder={t("enter_your_password")}
                onKeyPress={handleEnterKeyPress}
                onFocus={() => {
                  ReactGATracker(EventType.LOGIN_WITH_PASSWORD_FIELD_CLICK);
                }}
              />
              <LoginDetailsIconsContainer
                password
                src={LoginUserVIewPassword}
                alt="ValueMedi user password visibility icon"
                onClick={onChangeShowPassword}
              />
            </LoginDetailsInputContainer>
          </>
        )}
        <LoginOptionButtonContainer>
          <LoginOptionButton
            onClick={() => {
              setShowMobileNumberLogin(!showMobileNumberLogin);
              if (showMobileNumberLogin) {
                ReactGATracker(EventType.LOGIN_WITH_USER_NAME_CLICK);
              } else {
                ReactGATracker(EventType.LOGIN_WITH_PHONE_NUMBER_CLICK);
              }
            }}
          >
            <Trans
              i18nKey={
                showMobileNumberLogin
                  ? "login_with_user_name"
                  : "login_with_phone_number"
              }
            />
          </LoginOptionButton>
        </LoginOptionButtonContainer>
        <UserLoginButton
          loading={isPending || sendingOTP}
          disabled={isPending || sendingOTP}
          onClick={handleSubmit(
            showMobileNumberLogin ? onPhoneNumberLogin : onSubmit
          )}
        >
          <Trans i18nKey="login_to_valuemrp" />
          <LoginDetailsIconsContainer
            src={LoginIcon}
            alt="login icon for ValueMedi - Retailer/Distributor/Representative"
          />
        </UserLoginButton>
        {/* <RegisterAndForgotPasswordContainer>
          <BottomTextContainer
            onClick={() => {
              ReactGATracker(EventType.REGISTER_BUTTON_CLICK);
              navigate("/signup");
            }}
          >
            <Trans i18nKey="create_new_account" />
          </BottomTextContainer>
          <BottomTextContainer
            onClick={() => {
              ReactGATracker(EventType.FORGOT_PASSWORD_BUTTON_CLICK);
              navigate("/reset");
            }}
          >
            <Trans i18nKey="forgot_password" />?
          </BottomTextContainer>
        </RegisterAndForgotPasswordContainer> */}
        {/* <LoginFooter /> */}
      </LoginBodyContainer>
    </>
  );
};

export default Login;
